import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Route } from '../../utils/route';
import { TabsComponent } from '../../components/tabs/tabs.component';
import { DraftsTabComponent } from './drafts-tab/drafts-tab.component';
import { CustomersTabComponent } from './customers-tab/customers-tab.component';
import { DictionaryService } from '../../services/dictionary.service';

@Component({
  selector: 'app-customers',
  imports: [TabsComponent, DraftsTabComponent, CustomersTabComponent],
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.scss',
})
export class CustomersComponent extends Route implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private dictionaryService = inject(DictionaryService);

  private readonly topLevelTabs = [
    { key: 'Customers', param: 'customers' },
    { key: 'Drafts', param: 'drafts' },
  ] as const;

  defaultTab = signal<string>('');

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.defaultTab.set(this.toTopLevelTabLabel(params['tab'] || ''));
    });
  }

  onTabChange(tab: string): void {
    const tabParam = this.toTopLevelTabParam(tab);

    if (!tabParam) {
      return;
    }

    if (tabParam === 'drafts') {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { tab: tabParam },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        tab: tabParam,
        draftTab: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private toTopLevelTabLabel(tabParam: string): string {
    const match = this.topLevelTabs.find(({ key, param }) =>
      this.isTabMatch(key, tabParam, param),
    );
    return match ? this.dictionaryService.get(match.key) : '';
  }

  private toTopLevelTabParam(tabLabel: string): 'customers' | 'drafts' | '' {
    return (
      this.topLevelTabs.find(({ key, param }) =>
        this.isTabMatch(key, tabLabel, param),
      )?.param || ''
    );
  }

  private isTabMatch(
    key: 'Customers' | 'Drafts',
    value: string,
    stableValue: 'customers' | 'drafts',
  ): boolean {
    if (value.toLowerCase() === stableValue) {
      return true;
    }

    const translations = this.dictionaryService.dictionary[key];
    return Object.values(translations).includes(value);
  }
}
